import React from "react"
import { Box, Divider, useTheme, Typography, Button } from "@mui/material"
import { useTranslation } from "providers/i18n"
import { PageCard, PageCardContent } from "components/card"
import { AppNavigation } from "containers/AppNavigation"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import { QRCode } from "components/QRCode"
import { Image } from "components/Image"
import { MotionDiv } from "components/motions"
import { PromotionCard } from "components/domains/promotions"
import { Link, useParams } from "react-router-dom"
import { usePromotionDetail } from "hooks/promotion/usePromotionDetail"

export const PromotionDetailKioskHPage = ({ Layout = Box }) => {
  const params = useParams()
  const { promotionId } = params
  const {
    promotion,
    promotionDetailMobileUrl,
    morePromotions,
    navigateToLocation,
  } = usePromotionDetail(promotionId)

  const { t } = useTranslation()
  const theme = useTheme()

  const styleOverrides =
    theme.components[`PromotionDetailPage`]?.styleOverrides || {}
  const {
    pageCardContent,
    divider,
    mainWrapper,
    secondaryWrapper,
    promotionImage,
    contentContainer,
    venueLogo,
    locationButton,
    headerContainer,
    titleContainer,
    titleText,
    descriptionContainer,
    descriptionText,
    locationContainer,
    infoContainer,
    timeContainer,
    infoLabel,
    infoText,
    infoDivider,
    qrCode,
    gridContainer,
  } = styleOverrides

  const { styleOverrides: bannerStyleOverrides = {} } =
    theme.components[`AppBanner`] || {}
  const { root } = bannerStyleOverrides

  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { wrapper, body, footer } = sideBarStyleOverrides

  if (!promotion) return null
  return (
    <Layout
      aside={
        <PageCard>
          <Box sx={wrapper}>
            <Box sx={root}></Box>
            <Box sx={body}>
              <AppNavigation />
            </Box>
            <Box sx={footer}>
              <NavigationControl />
            </Box>
          </Box>
        </PageCard>
      }
    >
      <PageCard>
        <PageCardContent sx={pageCardContent}>
          <MotionDiv
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.25 }}
          >
            <Box sx={mainWrapper}>
              <Image sx={promotionImage} src={promotion.logo} />
              <Box sx={contentContainer}>
                <Box sx={headerContainer}>
                  {promotion.venueLogo && (
                    <Image sx={venueLogo} src={promotion.venueLogo} />
                  )}
                  <Box sx={titleContainer}>
                    <Typography variant="h5" sx={titleText}>
                      {t(promotion.name)}
                    </Typography>
                    {promotion.hasLocation && (
                      <Button
                        sx={locationButton}
                        variant="contained"
                        onClick={navigateToLocation}
                      >
                        {t("View Location")}
                      </Button>
                    )}
                  </Box>
                </Box>
                <Box sx={descriptionContainer}>
                  <Typography
                    sx={descriptionText}
                    varaint="body1"
                    dangerouslySetInnerHTML={{
                      __html: promotion.descriptionText,
                    }}
                  />
                </Box>
                <Box sx={infoContainer}>
                  <Box sx={locationContainer}>
                    <Typography sx={infoLabel}>{t("Location")}</Typography>
                    <Typography sx={infoText}>
                      {t(promotion.location)}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" sx={infoDivider} />
                  <Box sx={timeContainer}>
                    <Typography sx={infoLabel}>{t("Open")}</Typography>
                    <Typography sx={infoText}>
                      {t(promotion.openingText)}
                    </Typography>
                  </Box>
                  <QRCode
                    sx={qrCode}
                    url={promotionDetailMobileUrl}
                    caption={t("Send to Mobile")}
                  />
                </Box>
              </Box>
            </Box>
            <Divider sx={divider} />
            <Box sx={secondaryWrapper}>
              <Box sx={gridContainer}>
                {morePromotions.length > 0 &&
                  morePromotions.map((morePromotion) => (
                    <Link
                      key={morePromotion.id}
                      to={`/promotions/${morePromotion.id}`}
                    >
                      <PromotionCard promotion={morePromotion} />
                    </Link>
                  ))}
              </Box>
            </Box>
          </MotionDiv>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
